import {
  Comment,
  type ExtractPropTypes,
  Fragment,
  type InjectionKey,
  type VNode,
  defineComponent,
} from 'vue'

// Utils
import {
  createNamespace,
  extend,
  flat,
  makeArrayProp,
  makeNumericProp,
  pick,
  truthProp,
} from '@xzx-design/utils'

// Composables
import { useChildren, useSyncPropRef } from '@xzx-design/hooks'

// Components
import { Tab, Tabs } from '@xzx-design/components'
import Toolbar, {
  selectToolbarProps,
  selectToolbarSlots,
} from './select-toolbar'

const [name, bem] = createNamespace('select-group')

export type SelectGroupProvide = Record<string, string>

export const PICKER_GROUP_KEY: InjectionKey<SelectGroupProvide> = Symbol(name)

export const selectGroupProps = extend(
  {
    tabs: makeArrayProp<string>(),
    activeTab: makeNumericProp(0),
    nextStepText: String,
    showToolbar: truthProp,
  },
  selectToolbarProps
)

export type SelectGroupProps = ExtractPropTypes<typeof selectGroupProps>
export default defineComponent({
  name,
  __name: name,

  props: selectGroupProps,

  emits: ['confirm', 'cancel', 'update:activeTab'],

  setup(props, { emit, slots }) {
    const activeTab = useSyncPropRef(
      () => props.activeTab,
      (value) => emit('update:activeTab', value)
    )
    const { children, linkChildren } = useChildren(PICKER_GROUP_KEY)

    linkChildren()

    const showNextButton = () =>
      +activeTab.value < props.tabs.length - 1 && props.nextStepText

    const onConfirm = () => {
      if (showNextButton()) {
        activeTab.value = +activeTab.value + 1
      } else {
        emit(
          'confirm',
          children.map((item) => item.confirm())
        )
      }
    }

    const onCancel = () => emit('cancel')

    return () => {
      let childNodes = slots
        .default?.()
        ?.filter((node) => node.type !== Comment)
        .map((node) => {
          if (node.type === Fragment) {
            return node.children as VNode[]
          }

          return node
        })

      if (childNodes) {
        childNodes = flat(childNodes)
      }

      const confirmButtonText = showNextButton()
        ? props.nextStepText
        : props.confirmButtonText

      return (
        <div class={bem()}>
          {props.showToolbar ? (
            <Toolbar
              v-slots={pick(slots, selectToolbarSlots)}
              title={props.title}
              cancelButtonText={props.cancelButtonText}
              confirmButtonText={confirmButtonText}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          ) : null}
          <Tabs
            v-model:active={activeTab.value}
            class={bem('tabs')}
            shrink
            animated
            lazyRender={false}
          >
            {props.tabs.map((title, index) => (
              <Tab title={title} titleClass={bem('tab-title')}>
                {childNodes?.[index]}
              </Tab>
            ))}
          </Tabs>
        </div>
      )
    }
  },
})
